import {z} from '@hono/zod-openapi';

import {INTEREST_LEAD_QUALITIES} from '../constants/INTEREST_LEAD_QUALITIES';
import {INTEREST_SIGNAL_CODES} from '../constants/INTEREST_SIGNAL_CODES';
import {INTEREST_SIGNAL_EVIDENCE_OMISSIONS} from '../constants/INTEREST_SIGNAL_EVIDENCE_OMISSIONS';
import {INTEREST_SIGNAL_SEVERITIES} from '../constants/INTEREST_SIGNAL_SEVERITIES';
import {INTEREST_TIMELINE_EVENT_KINDS} from '../constants/INTEREST_TIMELINE_EVENT_KINDS';

/**
 * Wire mirror of the pure `InterestSignal` produced by `detectInterestSignals`.
 *
 * Signals are computed deterministically by the shared engine — never by the model. They travel on
 * the wire so the prompt can explain WHY a draft is being written (first reply vs. reply-to-message
 * vs. follow-up) without the model inferring it. Every enum is built from the same `as const` tuple
 * the pure types derive from, so this schema cannot drift from the engine.
 *
 * `evidence` carries only localisation parameters. It never carries a human-readable sentence —
 * reason copy is rendered and localised by the frontend from `code` plus these values.
 */
export const assistantInterestSignalSchema = z
  .object({
    code: z.enum(INTEREST_SIGNAL_CODES),
    severity: z.enum(INTEREST_SIGNAL_SEVERITIES),
    evaluatedAt: z.string().min(1),
    evidenceIncomplete: z.boolean(),
    evidenceOmissions: z.array(z.enum(INTEREST_SIGNAL_EVIDENCE_OMISSIONS)).default([]),
    evidence: z.object({
      waitingSinceHours: z.number().int().nonnegative().optional(),
      waitingSinceAt: z.string().min(1).optional(),
      inactiveForDays: z.number().int().nonnegative().optional(),
      lastActivityAt: z.string().min(1).optional(),
      triggeringEventId: z.string().min(1).optional(),
      overdueTaskCount: z.number().int().nonnegative().optional(),
      overdueBySinceDays: z.number().int().nonnegative().optional(),
      oldestOverdueAt: z.string().min(1).optional(),
      leadQuality: z.enum(INTEREST_LEAD_QUALITIES).optional(),
      engagementKind: z.enum(INTEREST_TIMELINE_EVENT_KINDS).optional(),
    }),
  })
  .openapi('AssistantInterestSignal');

export type AssistantInterestSignal = z.infer<typeof assistantInterestSignalSchema>;
